/** @typedef { import('./_types').User } User */
/** @typedef { import('./_types').AccountManager } AccountManager */
/** @typedef { import('src/shared/usePermissionCheck').Permissions } Permissions */

/** @type {(user?: User, label?: string) => string | undefined} */
export const getUserDisplayName = (user, label = 'Unknown') => {
  if (!user) return label;

  if (user.firstName || user.lastName) {
    return [user.firstName, user.lastName].join(' ');
  }
  if (user.nickname) {
    return user.nickname;
  }
  if (user.email) {
    return user.email;
  }
  return label;
};

// The primary trainer for a platform has a trainer_id set to themselves
// All users in the platform have the trainer_id set to the head trainer of the platform
/** @type {(user: User) => boolean} */
export const isHeadTrainer = (user) => {
  return user.trainerId === user.id;
};

// A subtrainer has a head trainer id (will be equal to the head trainer)
/** @type {(user: User) => boolean} */
export const isSubTrainer = (user) => {
  return !!user.headTrainerId;
};

/** @type {(user: User) => boolean} */
export const isProTrainer = (user) => {
  return user.payingTrainer || !!user.headTrainerId;
};

/** @type {(user: User) => boolean} */
export const isManager = (user) => {
  return !!(user.roles && user.roles.includes('manager'));
};

export const isAdmin = (user) => {
  return !!(user.roles && user.roles.includes('admin'));
};

export const isSuperAdmin = (user) => {
  return !!(user.roles && user.roles.includes('super_admin'));
};

export const isSupportRole = (user) => {
  return !!(user.roles && user.roles.includes('support'));
};

/** @type {(user: User) => boolean} */
export const isTrainer = (user) => isHeadTrainer(user) || isSubTrainer(user);

/** @type {(user: User) => string} */
export const getUserInitials = (user) => {
  if (user.firstName && user.lastName) {
    user.firstName?.charAt(0) + user.lastName?.charAt(0);
  }
  if (user.nickname) {
    user.nickname.charAt(0);
  }
  if (user.email) {
    return user.email.charAt(0);
  }
  return '00';
};

/** @type {(accountManager: AccountManager) => string} */
export const getDependentInitials = (accountManager) => {
  if (accountManager.dependentName.split(' ').length > 1) {
    return (
      accountManager.dependentName.split(' ')[0].charAt(0) +
      accountManager.dependentName.split(' ')[1].charAt(0)
    );
  } else if (accountManager.dependentEmail) {
    return accountManager.dependentEmail.charAt(0);
  } else {
    return '00';
  }
};

/** @type {(accountManager: AccountManager) => string | undefined} */
export const getDependentDisplayName = (accountManager) => {
  if (!accountManager) return 'Unknown';

  if (accountManager.dependentName) {
    return accountManager.dependentName;
  }
  if (accountManager.dependentEmail) {
    return accountManager.dependentEmail;
  }
  return 'Unknown';
};

/** @type {(user: User) => string} */
export const getUserAddress = (user) => {
  const addressParts = [
    user.streetAddress,
    user.city,
    user.state,
    user.zipCode,
  ];
  const nonEmptyParts = addressParts.filter(
    (part) => part && part.trim() !== ''
  );
  return nonEmptyParts.join(', ');
};

/** @type {(user: User, permissions: Permissions) => string} */
export const getClientAutocompleteKey = (user, permissions) => {
  if (permissions.canOnlyManageLocationClient && !!user.locationId) {
    return `client_location_${user.locationId}`;
  }
  if (permissions.canManageClientsForTrainer) {
    return `client_trainer_${user.trainerId}`;
  }
  return `client_trainer_${user.id}`;
};

export const statsForPlatform = (trainer) => {
  let stats = [];
  stats.push(...defaultStats);

  if (trainer.healthInfoMetrics) {
    stats.push(
      ...trainer.healthInfoMetrics.map((stat) => {
        return { stat: stat, label: stat, value: stat };
      })
    );
  }

  // defaultHealthMetrics are the stats we *DON'T* want to include
  return stats.filter((r) => !trainer.defaultHealthMetrics?.includes(r.label));
};
export const defaultStats = [
  { stat: 'weight', label: 'Weight', value: 'weight' },
  { stat: 'height', label: 'Height', value: 'height' },
  { stat: 'bmi', label: 'BMI', value: 'bmi' },
  { stat: 'systolic_bp', label: 'Systolic BP', value: 'systolic_bp' },
  { stat: 'diastolic_bp', label: 'Diastolic BP', value: 'diastolic_bp' },
  { stat: 'body_fat', label: 'Body Fat %', value: 'body_fat' },
  { stat: 'rhr', label: 'Resting Heart Rate', value: 'rhr' },
  { stat: 'waist', label: 'Waist Size', value: 'waist' },
  { stat: 'thigh', label: 'Thigh Size', value: 'thigh' },
  { stat: 'bicep', label: 'Bicep Size', value: 'bicep' },
  {
    stat: 'blood_pressure',
    label: 'Blood Pressure',
    value: null,
    metric: null,
  },
];
